/**
 * The composer.
 *
 * Eager, and deliberately outside `ThreadPrimitive`: the pane opens *above* it
 * and it must be usable before the thread chunk has downloaded. That placement
 * costs it assistant-ui's keybindings, so **Enter submitting is hand-wired
 * here** — remove it and the field silently stops sending on the one key
 * everybody uses.
 *
 * Orange ring, from the prototype (`.composer.hero`, v11.html 127–134): a 1px
 * primary border plus a 3px glow that brightens on focus. It is the only orange
 * outline on the page, which is what makes it read as the thing to use.
 */

import { cn } from '@workspace/ui/lib/utils'
import { ArrowUp, MessageSquare } from 'lucide-react'
import {
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useAskProfile } from '../context/ask-profile-context'
import {
  nextPlaceholderIndex,
  PLACEHOLDER_ROTATION_MS,
  placeholderAt,
  shouldRotatePlaceholder,
} from '../lib/placeholders'

/**
 * Mirrors `PROFILE_CHAT_QUESTION_MAX_LENGTH` in
 * `apps/backend/src/domain/share-link.models.ts`. Duplicated across the app
 * boundary on purpose, same as the preset labels: enforced here so a question
 * over the cap is never sent at all, rather than sent and 400'd by the
 * route's own Zod `.max()` — which is the "answer stopped early" failure this
 * guards against. Edit both together.
 */
export const QUESTION_MAX_LENGTH = 500

export interface AskProfileComposerProps {
  /** Pulls the thread chunk down on first intent, before the first click. */
  onWarm: () => void
}

export function AskProfileComposer({ onWarm }: AskProfileComposerProps) {
  const ask = useAskProfile()
  const [value, setValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const isRotating = shouldRotatePlaceholder({ isFocused, hasValue: value !== '' })
  useEffect(() => {
    if (!isRotating) return
    const timer = setInterval(
      () => setPlaceholderIndex(nextPlaceholderIndex),
      PLACEHOLDER_ROTATION_MS
    )
    return () => clearInterval(timer)
  }, [isRotating])

  // Destructured, matching the house pattern (`registerSender`/`registerClearer`
  // in ask-profile-thread.tsx): the context VALUE is a new object on most
  // renders, so depending on `ask` itself would re-register on nearly every
  // one. The composer is eager (unlike the sender/clearer it registers
  // alongside), so this is set before a chip or the send button can possibly
  // be clicked — no queueing needed, unlike `registerSender`.
  const { registerFocusComposer } = ask
  useEffect(() => {
    registerFocusComposer(() => inputRef.current?.focus())
    return () => registerFocusComposer(null)
  }, [registerFocusComposer])

  function submit() {
    const question = value.trim().slice(0, QUESTION_MAX_LENGTH)
    if (question === '') return
    setValue('')
    // Back to the resting placeholder: the examples exist to show what this
    // answers, and by now the investor has answered that for themselves.
    setPlaceholderIndex(0)
    ask.ask({ entry: 'typed', text: question })
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    submit()
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter' || event.shiftKey) return
    event.preventDefault()
    submit()
  }

  return (
    <form
      ref={ask.composerRef as RefObject<HTMLFormElement | null>}
      onSubmit={handleSubmit}
      className={cn(
        'mt-3.5 flex items-center gap-2.5 rounded-full border border-primary bg-[#0F0F10] py-2 pl-4 pr-2',
        'shadow-[0_0_0_3px_rgba(249,115,22,0.12)] focus-within:shadow-[0_0_0_3px_rgba(249,115,22,0.28)]'
      )}
    >
      {/* Says what the box is before anything is typed. Decorative: the input
          already carries the accessible name. */}
      <MessageSquare className="size-4 flex-none text-muted-foreground" aria-hidden />
      <input
        ref={inputRef}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsFocused(true)
          onWarm()
        }}
        onPointerEnter={onWarm}
        onBlur={() => setIsFocused(false)}
        aria-label="Ask anything about this company"
        placeholder={placeholderAt(placeholderIndex)}
        // The hard cap: the browser will not accept a keystroke or a paste
        // past it, so a question this long can never reach `submit()` at
        // all — not even truncated, since the field itself stops growing.
        maxLength={QUESTION_MAX_LENGTH}
        className="min-w-0 flex-1 bg-transparent py-2 text-[15px] text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
      />
      {/* Silent until it matters: a counter on every keystroke is noise for a
          nine-word question, which is nearly all of them. */}
      {value.length > QUESTION_MAX_LENGTH - 60 && (
        <span className="flex-none text-[11px] tabular-nums text-muted-foreground">
          {QUESTION_MAX_LENGTH - value.length}
        </span>
      )}
      <button
        type="submit"
        aria-label="Send"
        disabled={value.trim() === ''}
        // Circular, not a squircle: a 10px-radius square sitting inside a pill
        // reads as two different shape languages in one control. The pill's
        // radius is half its height, so the button's has to be too.
        className="grid size-10 flex-none cursor-pointer place-items-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        <ArrowUp className="size-4" />
      </button>
    </form>
  )
}
